import { useMutation, useQueryClient } from '@tanstack/react-query';

import { queryKeysSupplier } from '../queryKeys';

import { addSupplierResponsiblePerson } from './addSupplierResponsiblePerson';
import { type AddSupplierResponsiblePersonMutationOptions } from './types';

/**
 * React Query based custom hook for adding a responsible person to a
 * supplier relationship.
 *
 * Invalidates the supplier detail query for the affected data exchange ID on settle.
 *
 * @param options - Optional mutation options.
 * @returns The mutation result.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Suppliers/operation/add_supplier_responsible_person_suppliers__de_id__responsible_persons_post
 */
export const useMutationAddSupplierResponsiblePerson = (
  options?: AddSupplierResponsiblePersonMutationOptions,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ deId, payload }) =>
      addSupplierResponsiblePerson(deId, payload),
    onSettled(_data, _error, { deId }) {
      void queryClient.invalidateQueries({
        queryKey: queryKeysSupplier.getDetail(deId),
      });
    },
    throwOnError: false,
    ...options,
  });
};
